import { FC, useMemo } from "react";
import i18n from "@/i18n";
import FloatButton from "../../components/FloatButton";
import { PlusOutlined } from "@ant-design/icons";
import { ListView as IListView } from "@data-root/types";
import {
  Inline,
  UseRowsContext,
  useAppContext,
  useAsyncCallback,
  useClientContext,
  useNavigation,
  useRows,
  useViewFilters,
  ViewFooter,
  ViewHeader,
  useGroups,
  UseGroupsContext,
  useViewContext,
} from "@data-root/react";
import { ScreenProps } from "../../typings";
import { List } from "../../components/inline/List";
import { Layout, Typography } from "antd";
import { v4 } from "uuid";
import { ErrorView } from "@data-root/error-boundary";

const { Content } = Layout;
const { Text } = Typography;

export const ListView: FC<ScreenProps<IListView>> = ({ view }) => {
  const { tableId, options, noAdd } = view;

  const { core } = useClientContext();
  const { appSpecifier, styles, getTable } = useAppContext();
  const { navigation } = useNavigation();

  const table = useMemo(() => getTable(tableId), [getTable, tableId]);

  const { filters: contextFilters, sort: contextSort } = useViewContext();
  const { filters: viewFilters, sort: viewSort } = useViewFilters(view);

  const groupsContext = useGroups(core, appSpecifier, tableId);
  const rowsContext = useRows(
    core,
    appSpecifier,
    tableId,
    [...contextFilters, ...viewFilters, ...groupsContext.filters],
    [...contextSort, ...viewSort]
  );

  const {
    getContext,
    displayRows,
    loaded,
    fetch,
    fetching,
    create,
    _delete,
    totalRows,
    error,
  } = rowsContext;

  const onNew = useAsyncCallback(async () => {
    const context = getContext();
    const { rowId } = await create({});
    const onNew = await core.evaluateFormula<string>(options.onNew, context);
    if (onNew) navigation.navigate(onNew, { rowId });
  }, [core, create, getContext, navigation, options.onNew]);

  const containerId = useMemo(() => v4(), []);

  if (error) return <ErrorView error={error} />;

  return (
    <UseRowsContext.Provider value={rowsContext}>
      <UseGroupsContext.Provider value={groupsContext}>
        <Inline table={table} view={view} onDelete={(row) => _delete(row.id)}>
          <Layout>
            <ViewHeader view={view} getContext={getContext} />

            <Content id={containerId} style={{ overflow: "auto", padding: 10 }}>
              <List
                containerId={containerId}
                getContext={getContext}
                loaded={loaded}
                totalRows={totalRows}
                rows={displayRows}
                fetching={fetching}
                onFetch={fetch}
              />
            </Content>

            <ViewFooter>
              <Text strong>{totalRows} {i18n.t("records")}</Text>
            </ViewFooter>

            {!noAdd && (
              <FloatButton
                shape="square"
                onClick={onNew}
                icon={<PlusOutlined style={{ color: styles.darkTintColor }} />}
              />
            )}
          </Layout>
        </Inline>
      </UseGroupsContext.Provider>
    </UseRowsContext.Provider>
  );
};
